import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthProvider';
import { useBlogContext } from '../../context/BlogContext';
import { useCollageLinks } from '../../hooks/useCollageLinks';
import { collageData } from '../../config/collageData';
import { supabaseClient } from '../../config/supabase-client';
import './ManagePage.css';

interface PublishedBlog {
    id: string;
    title: string;
}

const ManagePage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Login state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState<string | null>(null);
    const [loggingIn, setLoggingIn] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoggingIn(true);
        setLoginError(null);
        const { error } = await supabaseClient.auth.signInWithPassword({ email, password });
        if (error) setLoginError(error.message);
        setLoggingIn(false);
    };

    if (!user) {
        return (
            <div className="manage-page">
                <div className="manage-login">
                    <h1>Manage</h1>
                    <form onSubmit={handleLogin}>
                        <label>
                            Email
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="username" required />
                        </label>
                        <label>
                            Passcode
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required />
                        </label>
                        {loginError && <p className="manage-error">{loginError}</p>}
                        <button type="submit" disabled={loggingIn}>
                            {loggingIn ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-page">
            <div className="manage-container">
                <h1>Manage</h1>
                <BlogSection navigate={navigate} />
                <CollageLinksSection />
            </div>
        </div>
    );
};

// ─── Blog Management Section ───────────────────────────────────────────────

const BlogSection: React.FC<{ navigate: ReturnType<typeof useNavigate> }> = ({ navigate }) => {
    const { blogs, draftBlogs, publishDraft, setAsDraft, deleteBlog } = useBlogContext();

    const allPosts = [
        ...draftBlogs.map(b => ({ ...b, _status: 'draft' as const })),
        ...blogs.map(b => ({ ...b, _status: 'published' as const })),
    ];

    const handleDelete = (id: string, title: string) => {
        if (window.confirm(`Delete "${title}"?`)) {
            deleteBlog(id);
        }
    };

    return (
        <>
            <h2>Blog Posts</h2>
            {allPosts.length === 0 && <p style={{ color: '#888' }}>No posts yet.</p>}
            {allPosts.length > 0 && (
                <table className="manage-blog-table">
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allPosts.map(post => (
                            <tr key={post.id}>
                                <td>{post.title || '(untitled)'}</td>
                                <td>
                                    <span className={post._status === 'draft' ? 'status-draft' : 'status-published'}>
                                        {post._status}
                                    </span>
                                </td>
                                <td>
                                    <button className="manage-action" onClick={() => navigate(`/manage/edit/${post.id}`)}>
                                        edit
                                    </button>
                                    {post._status === 'draft' ? (
                                        <button className="manage-action" onClick={() => publishDraft(post.id)}>
                                            publish
                                        </button>
                                    ) : (
                                        <button className="manage-action" onClick={() => setAsDraft(post.id)}>
                                            unpublish
                                        </button>
                                    )}
                                    <button className="manage-action manage-action-danger" onClick={() => handleDelete(post.id, post.title)}>
                                        delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <button className="manage-new-btn" onClick={() => navigate('/manage/edit/new')}>
                + new post
            </button>
        </>
    );
};

// ─── Collage Links Section ─────────────────────────────────────────────────

const CollageLinksSection: React.FC = () => {
    const { linkMap, loading, updateLink } = useCollageLinks();
    const [blogs, setBlogs] = useState<PublishedBlog[]>([]);
    const [draftLinks, setDraftLinks] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState<Record<string, boolean>>({});
    const [saveError, setSaveError] = useState<Record<string, string | null>>({});
    const [saveSuccess, setSaveSuccess] = useState<Record<string, boolean>>({});

    useEffect(() => {
        async function fetchBlogs() {
            const { data, error } = await supabaseClient
                .from('blogs')
                .select('id, title')
                .eq('is_draft', false)
                .order('published_at', { ascending: false });
            if (!error && data) setBlogs(data);
        }
        fetchBlogs();
    }, []);

    const blogSlug = (title: string) =>
        `/blog/${encodeURIComponent(title.toLowerCase().replace(/\s+/g, '-'))}`;

    const keyFor = (collageId: string, alt: string) => `${collageId}::${alt}`;

    const currentLink = (collageId: string, alt: string, staticLink: string) => {
        const key = keyFor(collageId, alt);
        if (draftLinks[key] !== undefined) return draftLinks[key];
        return linkMap[key] ?? staticLink;
    };

    const handleSave = async (collageId: string, alt: string, staticLink: string) => {
        const key = keyFor(collageId, alt);
        const link = currentLink(collageId, alt, staticLink);
        setSaving(prev => ({ ...prev, [key]: true }));
        setSaveError(prev => ({ ...prev, [key]: null }));
        setSaveSuccess(prev => ({ ...prev, [key]: false }));

        const err = await updateLink(collageId, alt, link);

        setSaving(prev => ({ ...prev, [key]: false }));
        if (err) {
            setSaveError(prev => ({ ...prev, [key]: err }));
        } else {
            setSaveSuccess(prev => ({ ...prev, [key]: true }));
            setTimeout(() => setSaveSuccess(prev => ({ ...prev, [key]: false })), 2000);
        }
    };

    return (
        <>
            <h2>Collage Links</h2>
            {loading && <p style={{ color: '#888' }}>Loading links…</p>}
            <ul className="manage-links-list">
                {collageData.map(collage =>
                    collage.clickableItems.map(item => {
                        const key = keyFor(collage.id, item.alt ?? '');
                        const link = currentLink(collage.id, item.alt ?? '', item.link);
                        return (
                            <li key={key} className="manage-link-row">
                                <img src={item.image} alt={item.alt ?? 'item'} className="manage-link-thumb" />
                                <span className="manage-link-alt">{item.alt}</span>
                                <div className="manage-link-controls">
                                    <input
                                        type="url"
                                        value={link}
                                        onChange={e => setDraftLinks(prev => ({ ...prev, [key]: e.target.value }))}
                                        placeholder="https://…"
                                    />
                                    {blogs.length > 0 && (
                                        <select
                                            value=""
                                            onChange={e => {
                                                if (e.target.value) setDraftLinks(prev => ({ ...prev, [key]: e.target.value }));
                                            }}
                                        >
                                            <option value="">— or pick a blog post —</option>
                                            {blogs.map(b => (
                                                <option key={b.id} value={blogSlug(b.title)}>{b.title}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <button
                                    className="manage-link-save"
                                    disabled={saving[key]}
                                    onClick={() => handleSave(collage.id, item.alt ?? '', item.link)}
                                >
                                    {saving[key] ? 'Saving…' : saveSuccess[key] ? 'Saved ✓' : 'Save'}
                                </button>
                                {saveError[key] && <span className="manage-link-error">{saveError[key]}</span>}
                            </li>
                        );
                    })
                )}
            </ul>
        </>
    );
};

export default ManagePage;
